"use client";

import { SupportedLanguage } from "../../config/languages";
import { Link } from "../Link/Link";
import { CloseIcon } from "../icons/CloseIcon";
import classNames from "classnames/bind";
import { useEffect, useState } from "react";
import styles from "./PromoPopup.module.css";

const cx = classNames.bind(styles);

const COOKIE_NAME = "entrix_promo_dismissed";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days
const SLIDE_IN_DELAY = 2000;

// The dismissal is keyed to the current promo content so that a new promo
// re-appears even if a visitor dismissed the previous one within the 30 days.
const isDismissed = (dismissKey: string) =>
  document.cookie
    .split(";")
    .some((entry) => entry.trim() === `${COOKIE_NAME}=${dismissKey}`);

const rememberDismissal = (dismissKey: string) => {
  document.cookie = `${COOKIE_NAME}=${dismissKey}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
};

export interface PromoPopupCta {
  label: string;
  href: string;
  openInNewTab: boolean;
}

export interface PromoPopupProps {
  headline?: string | null;
  copy: string;
  cta?: PromoPopupCta;
  locale: SupportedLanguage;
  /** Identifies the current promo content so a new promo bypasses an old dismissal */
  dismissKey: string;
}

export const PromoPopup = ({
  headline,
  copy,
  cta,
  locale,
  dismissKey,
}: PromoPopupProps) => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isDismissed(dismissKey)) {
      return;
    }

    setMounted(true);

    const timer = setTimeout(() => setOpen(true), SLIDE_IN_DELAY);
    return () => clearTimeout(timer);
  }, [dismissKey]);

  if (!mounted) {
    return null;
  }

  const closeLabel = locale === "de" ? "Schließen" : "Close";

  const dismiss = () => {
    rememberDismissal(dismissKey);
    setOpen(false);
  };

  return (
    <div
      className={cx("wrapper")}
      data-open={open}
      role="dialog"
      aria-label={headline ?? copy}
      inert={!open}
    >
      <div className={cx("popup")}>
        <div className={cx("body")}>
          <div className={cx("header")}>
            {headline ? <p className={cx("headline")}>{headline}</p> : null}
            <button
              type="button"
              className={cx("close")}
              onClick={dismiss}
              aria-label={closeLabel}
            >
              <CloseIcon className={cx("closeIcon")} />
            </button>
          </div>
          <p className={cx("copy")}>{copy}</p>
        </div>
        {cta ? (
          <Link
            href={cta.href}
            locale={locale}
            openInNewTab={cta.openInNewTab}
            className={cx("cta")}
            onClick={() => rememberDismissal(dismissKey)}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </div>
  );
};
